import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, googleLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Handle Emergent Google OAuth redirect (session_id in URL hash)
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        (async () => {
          try {
            setLoading(true);
            await googleLogin(sid);
            window.history.replaceState({}, "", window.location.pathname);
            nav("/dashboard");
          } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
          } finally { setLoading(false); }
        })();
      }
    }
  }, [googleLogin, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav(loc.state?.from || "/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Sign in failed");
    } finally { setLoading(false); }
  };

  const startGoogle = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="login-page">
      <h1 className="font-serif text-4xl text-[#F7ECEB]">Welcome back.</h1>
      <p className="text-[#A89896] mt-3">Sign in to your private membership.</p>

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#C93B3B] mb-2">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 border border-[#382A2A] rounded-xl bg-[#1F1717] focus:border-[#C93B3B] outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#C93B3B] mb-2">Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 border border-[#382A2A] rounded-xl bg-[#1F1717] focus:border-[#C93B3B] outline-none"
          />
        </div>
        {err && <div className="text-sm text-red-700" data-testid="login-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-4 text-xs text-[#A89896] uppercase tracking-[0.2em]">
        <div className="flex-1 h-px bg-[#382A2A]" /> or <div className="flex-1 h-px bg-[#382A2A]" />
      </div>

      <button onClick={startGoogle} className="btn-ghost w-full" data-testid="login-google">
        Continue with Google
      </button>

      <p className="text-sm text-[#A89896] mt-8 text-center">
        New to Touche? <Link to="/register" className="text-[#C93B3B] underline" data-testid="login-to-register">Create an account</Link>
      </p>
    </div>
  );
}
